import React, { useEffect } from "react";
import { Redirect, Route, Switch, useLocation } from "polyrouter";
import AppRoute from "./AppRoute";
import { isMobile } from "react-device-detect";

import { Topbar } from "views/Topbar/Topbar";
import { SizeAlert } from "views/SizeAlert/SizeAlert";

import About from "./About/About";
import Home from "routes/Home/Home";
import RegisterDevice from "routes/Manage/Register/RegisterDevice";
import WelcomeModal from "views/WelcomeModal/WelcomeModal";

import AdminRouter from "./Admin/AdminRouter";
import InsightsRouter from "./Insights/InsightsRouter";
import ManageRouter from "./Manage/ManageRouter";
import AppErrorBoundary from "./AppErrorBoundary";
import { ErrorPage, LoadingIndicator, TenantsProvider } from "elements";
import { THEME_DARK, THEME_DEFAULT, THEME_LIGHT } from "../store/userMeta";
import { useSelector } from "react-redux";
import { State } from "store";
import { hasBackgroundImage } from "../utilities/misc";
import { ProtectedAppRoute } from "../TsRoleGuards";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import { isNew } from "../Constants";
import CompleteProfile from "./CompleteProfile/CompleteProfile";
import { AcceptInvite } from "./AcceptInvite/AcceptInvite";
import { PreloadCatalogImages } from "../elements/PreloadCatalogImages";
import { AuthWatcher, defaultAuthOptions } from "auth";
import { useEmailVerifiedQuery } from "generated/graphql";
import { ClaritiRouter } from "./Clariti/ClaritiRouter";

const DesktopRoutes = () => {
  // Note:  FeatureFlagEnabled doesn't render as expected when used as a router component

  return (
    <Switch>
      <Route path="/complete-profile" component={CompleteProfile} />
      <Route path="/accept-invite" component={AcceptInvite} />
      <AppRoute path="/home" Component={Home} />
      <ProtectedAppRoute
        path="/go"
        Component={RegisterDevice}
        requirement="register:device"
      />
      <AppRoute path="/admin" Component={AdminRouter} />
      <AppRoute path="/insights" Component={InsightsRouter} />
      <AppRoute path="/manage" Component={ManageRouter} />
      <AppRoute path="/clariti" Component={ClaritiRouter} />
      <AppRoute path="/about" Component={About} />
      <Route path="/">
        <Redirect to="/home" />
      </Route>
    </Switch>
  );
};

const MobileRoutes = () => (
  <Switch>
    <Route path="/complete-profile" component={CompleteProfile} />
    <Route path="/accept-invite" component={AcceptInvite} />
    <AppRoute path="/about" Component={About} />
    <ProtectedAppRoute
      path="/manage/register/"
      Component={RegisterDevice}
      requirement="register:device"
    />
    <ProtectedAppRoute
      path="/go"
      Component={RegisterDevice}
      requirement="register:device"
    />
    <Route>
      <SizeAlert />
    </Route>
  </Switch>
);

/**
 * Route through the authenticated areas of the application.
 * REQUIRES AUTHENTICATION, eg: From withAuthenticationRequired
 */
const AppRouter: React.FC = () => {
  const { pathname } = useLocation();
  const { user } = useAuth0();
  const theme = useSelector((state: State) => {
    return state.userMeta?.theme ?? THEME_DEFAULT;
  }).toLowerCase();
  const { data, fetching } = useEmailVerifiedQuery();

  const backgroundStyle = hasBackgroundImage(pathname, theme);

  useEffect(() => {
    const remove =
      theme === THEME_DARK.toLowerCase()
        ? THEME_LIGHT.toLowerCase()
        : THEME_DARK.toLowerCase();
    const bodyClasses = document.body.classList;
    bodyClasses.remove(`theme-${remove}`);
    bodyClasses.add(`theme-${theme}`);
  }, [theme]);

  if (fetching) {
    return (
      <LoadingIndicator
        className="theme-independent"
        title="Initializing Auth Connection"
      />
    );
  }

  if (
    user &&
    !user[isNew] &&
    !user.email_verified &&
    !data?.user.user?.email_verified
  ) {
    return <Redirect to="/verify" />;
  }
  return (
    <TenantsProvider>
      <div className={`theme-${theme}`}>
        <AuthWatcher />
        <Topbar />
        {!isMobile ? (
          <div className="main" style={backgroundStyle}>
            <AppErrorBoundary errorComponent={<ErrorPage />}>
              <DesktopRoutes />
              <WelcomeModal />
              <SizeAlert />
            </AppErrorBoundary>
          </div>
        ) : (
          <MobileRoutes />
        )}
      </div>
      <PreloadCatalogImages />
    </TenantsProvider>
  );
};

export default withAuthenticationRequired(AppRouter, defaultAuthOptions);
